/**
 * The onboarding card and its target pulse (doc 19), drawn on the existing UI
 * pixel buffer. No new layer, no new surface, no tutorial screen — doc 11's
 * standing rule is that the game never interrupts with a wall of text.
 *
 * This module knows nothing about the sim or the tutor's state machine: it is
 * handed a title, a body, and a list of already-projected screen points to
 * pulse, and it draws them. That is what keeps it in the `ui` layer.
 */

import { font } from '../render/font/font.ts';
import { isoDiamondRim } from '../render/primitives.ts';
import { PixelSurface } from '../render/surface.ts';
import { wrapText } from './menu.ts';
import { g, UI } from './theme.ts';
import { drawPanel, type Rect } from './widgets.ts';

export interface NudgeDraw {
  title: string;
  body: string;
  /** Gate cards stay until obeyed, and say so instead of offering a dismissal. */
  gate: boolean;
  /** UI-space points to pulse — ore veins, or the shoal the Lure should take. */
  targets: Array<{ x: number; y: number }>;
  /** 2-frame pulse parity (doc 19: pixel-honest, never a smooth glow). */
  parity: 0 | 1;
  /** Everything below this line is hotbar; the card docks above it. */
  hotbarTop: number;
}

const CARD_W = 260;
const PAD = g(3);

/** Card width is fixed; height follows the copy. Returns the card's rect. */
export function nudgeRect(s: PixelSurface, d: NudgeDraw): Rect {
  const inner = CARD_W - PAD * 2;
  const title = font('header');
  const body = font('body');
  const lines = wrapText('body', d.body, inner);
  const h = PAD + title.capHeight + g(2) + lines.length * (body.capHeight + g(1)) + g(2) + font('micro').capHeight + PAD;

  const x = (s.width - CARD_W) >> 1;
  // doc 19: the card never covers its own subject. Anything it points at in the
  // lower half of the viewport pushes the card to the top instead.
  const low = d.targets.some((t) => t.y > s.height / 2);
  const y = low ? g(10) : Math.max(g(4), d.hotbarTop - h - g(3));
  return { x, y, w: CARD_W, h };
}

export function drawNudge(s: PixelSurface, d: NudgeDraw): Rect {
  for (const t of d.targets) pulse(s, t.x, t.y, d.parity);

  const r = nudgeRect(s, d);
  drawPanel(s, r, { raised: true });
  // a gate wears the accent on its rim: this one is not going away on its own
  if (d.gate) s.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2, UI.accent);

  const title = font('header');
  const body = font('body');
  const micro = font('micro');
  let y = r.y + PAD;
  title.draw(s, d.title, r.x + PAD, y, UI.ink, { caps: true, letterSpacing: 1 });
  y += title.capHeight + g(2);
  for (const line of wrapText('body', d.body, r.w - PAD * 2)) {
    body.draw(s, line, r.x + PAD, y, UI.inkDim);
    y += body.capHeight + g(1);
  }
  y += g(2);
  micro.draw(
    s,
    d.gate ? 'DO IT AND THIS GOES AWAY' : 'CLICK OR ESC TO DISMISS',
    r.x + PAD,
    y,
    d.gate ? UI.accent : UI.inkDim,
    { caps: true, letterSpacing: 1 },
  );
  return r;
}

/** A tile rim that blinks on two frames — never a fade, never a smooth glow. */
function pulse(s: PixelSurface, x: number, y: number, parity: 0 | 1): void {
  if (parity === 1) return;
  isoDiamondRim(s, Math.round(x), Math.round(y), 8, UI.accent);
  isoDiamondRim(s, Math.round(x), Math.round(y), 11, UI.accent, true, 0);
}
